import { PlainTextMath } from '@/components/axi/PlainTextMath'
import AvatarWithName from '@/components/ui/avatars/avatar-w-name'
import { CommonMarkdownLatexComponent } from '@/components/ui/common/lessons'
import Latex from 'react-latex-next'
import { IAiConversationMessageUser, IUser } from '@/types/firebase'
import Image from 'next/image'
import React, { FC, Fragment } from 'react'
import { UserIcon } from '@heroicons/react/24/solid'

// MathLive stores composer messages in its own dialect - an outer
// \displaylines{...} wrapper, \textasciicircum-style macros, and an
// asymmetric "$ ... $$" delimiter pair - which KaTeX (react-latex-next)
// cannot parse, so bubbles fell back to raw source. Normalize the wrapper
// to clean $$...$$ display math with \begin{gathered}...\end{gathered};
// anything without \displaylines passes through untouched.
const sanitizeMathliveForKatex = (text: string): string => {
  let out = text
  if (/\\displaylines\{/.test(out)) {
    // strip the outer delimiters entirely (single $ open, double $$ close)
    out = out
      .trim()
      .replace(/^\$+\s*/, '')
      .replace(/\s*\$+$/, '')
    out = out.replace(/\\displaylines\{/g, '\\begin{gathered}')
    // the wrapper's closing brace is the last } in the string
    out = out.replace(/\}\s*$/, '\\end{gathered}')
    out = `$$ ${out} $$`
  }
  out = out.replace(/\\textasciicircum/g, '\\^{}')
  out = out.replace(/\\textasciitilde/g, '\\~{}')
  return out
}

interface Props {
  user?: IUser
  message: IAiConversationMessageUser
  handleImageClick?: (urls: string[], selectedImageIndex: number) => void
}

// Returns the prose when a composer message is nothing but \text{...} lines
// (optionally inside a gathered block); null when it contains any real maths.
function plainProseFromMathlive(raw: string): string | null {
  if (!raw || !raw.includes('\\text{')) {
    return null
  }

  const inner = raw
    .trim()
    .replace(/^\$+\s*/, '')
    .replace(/\s*\$+$/, '')
    .trim()
    .replace(/^\\begin\{gathered\}/, '')
    .replace(/\\end\{gathered\}$/, '')
    .trim()

  const lines = inner.split(/\\\\/)
  const out: string[] = []

  for (const line of lines) {
    const m = line.trim().match(/^\\text\{([^{}\\]*)\}$/)

    if (!m) {
      return null
    }

    out.push(m[1])
  }

  return out.join('\n')
}

// Prose that landed in the math field without \text{} (Mike, 23 Sep: a
// pasted sentence "Decimal 2 (or 2.0), and as a fraction it's just 2"
// rendered as italic maths with the spaces eaten). A student's own words
// must read as words: when the message has no LaTeX commands, no
// superscripts/subscripts and at least two real words, show it as plain
// text (outer $ delimiters dropped). Real formulas keep the maths pipeline.
function looksLikePlainProse(raw: string): string | null {
  if (!raw) {
    return null
  }

  const stripped = raw
    .trim()
    .replace(/^\$+\s*/, '')
    .replace(/\s*\$+$/, '')
    .trim()
    // MathLive's rewrites of ordinary punctuation and spacing
    .replace(/\\left\s*([([])/g, '$1')
    .replace(/\\right\s*([)\]])/g, '$1')
    .replace(/\^\{?\\prime\}?/g, "'")
    .replace(/\\[ :;,]/g, ' ')
    .replace(/\s{2,}/g, ' ')

  if (/[\\^_{}]/.test(stripped) || stripped.includes('$')) {
    return null
  }

  const words = stripped.match(/[A-Za-z]{2,}/g) || []

  return words.length >= 2 ? stripped : null
}

// Mixed words + maths from the composer's Text mode. MathLive stores a typed
// answer like "so x^2 + 3x - 10 = 48" as \text{so x\textasciicircum 2 ...},
// sometimes with real maths between \text{} groups. KaTeX renders \text{}
// with non-breaking spaces, so the whole answer became one clipped line with
// "x^2" as an accent. Here the words render as normal wrapping text (a
// typed ^ becomes a real superscript) and only the maths parts are typeset.
// Returns null when there is no \text{} group - pure formulas keep the
// existing maths pipeline.
type TMixedPart = { kind: 'text' | 'math'; value: string }

const TEXT_MODE_ESCAPES: [RegExp, string][] = [
  [/\\textasciicircum(\{\})?\s?/g, '^'],
  [/\\textasciitilde(\{\})?\s?/g, '~'],
  [/\\textbackslash(\{\})?\s?/g, '\\'],
  [/\\textquoteleft(\{\})?\s?/g, '\u2018'],
  [/\\textquoteright(\{\})?\s?/g, '\u2019'],
  [/\\textquotedblleft(\{\})?\s?/g, '\u201c'],
  [/\\textquotedblright(\{\})?\s?/g, '\u201d'],
  [/\\textendash(\{\})?\s?/g, '\u2013'],
  [/\\textemdash(\{\})?\s?/g, '\u2014'],
  [/\\([{}_&%$#])/g, '$1'],
  [/\\ /g, ' '],
]

const decodeTextModeText = (text: string): string =>
  TEXT_MODE_ESCAPES.reduce((acc, [re, to]) => acc.replace(re, to), text)

function splitMathliveLine(line: string): TMixedPart[] {
  const parts: TMixedPart[] = []
  let math = ''
  let i = 0

  while (i < line.length) {
    if (line.startsWith('\\text{', i)) {
      let depth = 1
      let j = i + 6

      while (j < line.length && depth > 0) {
        if (line[j] === '\\') {
          j += 2
          continue
        }
        if (line[j] === '{') depth++
        else if (line[j] === '}') depth--
        j++
      }

      if (math.trim()) parts.push({ kind: 'math', value: math.trim() })
      math = ''
      parts.push({
        kind: 'text',
        value: decodeTextModeText(line.slice(i + 6, Math.max(i + 6, j - 1))),
      })
      i = j
    } else {
      math += line[i]
      i++
    }
  }

  if (math.trim()) parts.push({ kind: 'math', value: math.trim() })

  return parts
}

export function mixedPartsFromMathlive(raw: string): TMixedPart[][] | null {
  if (!raw || !raw.includes('\\text{')) {
    return null
  }

  let inner = raw
    .trim()
    .replace(/^\$+\s*/, '')
    .replace(/\s*\$+$/, '')
    .trim()

  const wrapper = inner.match(/^\\displaylines\{([\s\S]*)\}$/)
  if (wrapper) inner = wrapper[1]
  inner = inner
    .replace(/^\\begin\{gathered\}/, '')
    .replace(/\\end\{gathered\}$/, '')
    .trim()

  const lines = inner
    .split(/\\\\/)
    .map((line) => splitMathliveLine(line.trim()))
    .filter((parts) => parts.length > 0)

  return lines.length ? lines : null
}

// "x^2", "x^{10}", "10^-3" typed as plain characters -> base + <sup>.
function renderTypedSuperscripts(text: string, keyPrefix: string) {
  const nodes: React.ReactNode[] = []
  const re = /\^(?:\{([^{}]*)\}|(-?[A-Za-z0-9.]+))/g
  let last = 0
  let m: RegExpExecArray | null

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index))
    nodes.push(<sup key={`${keyPrefix}-${m.index}`}>{m[1] ?? m[2]}</sup>)
    last = m.index + m[0].length
  }
  if (last < text.length) nodes.push(text.slice(last))

  return nodes
}

const MixedMathliveMessage: FC<{ lines: TMixedPart[][] }> = ({ lines }) => (
  <div className="text-sm text-v8-ink break-words">
    {lines.map((parts, li) => (
      <div key={li} className="whitespace-pre-wrap">
        {parts.map((part, pi) =>
          part.kind === 'text' ? (
            <Fragment key={pi}>
              {renderTypedSuperscripts(part.value, `${li}-${pi}`)}
            </Fragment>
          ) : (
            <Latex key={pi}>{`$${part.value}$`}</Latex>
          ),
        )}
      </div>
    ))}
  </div>
)

export const AiAssistantUserMessage: FC<Props> = ({
  user,
  message: { content, id },
  handleImageClick,
}) => {
  const nameForAvatarInitials: string | undefined = user?.name || user?.username

  return (
    <div className="flex flex-col-reverse sm:flex-row items-end sm:items-start justify-end gap-1">
      <div className="max-w-full sm:max-w-[90%] flex">
        {/* Wrapper for message and pointer */}
        <div className="max-w-full relative">
          {/* Message */}
          <div
            className={`px-2.5 py-2 flex flex-col text-left bg-lime-200 rounded-md overflow-hidden`}
          >
            {/* Message Text */}
            {content.text && (
              <>
                {content.isQuickResponsePrompt ? (
                  <CommonMarkdownLatexComponent text={content.text} />
                ) : content.plainText ? (
                  // Multi-line plain-text seeds (Ask-Axi context) - the math
                  // field mangles them into no-space italic
                  <PlainTextMath text={content.text} />
                ) : content.isRecommendationMessage ? (
                  // Render recommendation messages as plain text with user-friendly display
                  <div className="text-sm text-v8-ink whitespace-pre-wrap">
                    {content.displayText || content.text}
                  </div>
                ) : looksLikePlainProse(content.text) !== null ? (
                  <div className="text-sm text-v8-ink whitespace-pre-wrap break-words">
                    {looksLikePlainProse(content.text)}
                  </div>
                ) : plainProseFromMathlive(
                    sanitizeMathliveForKatex(content.text),
                  ) !== null ? (
                  // Text-mode composer input arrives as
                  // \begin{gathered}\text{...}\end{gathered}. KaTeX renders
                  // \text{} with non-breaking spaces, so a sentence never
                  // wraps and gets clipped on phones. Pure prose renders as
                  // wrapped plain text instead.
                  <div className="text-sm text-v8-ink whitespace-pre-wrap break-words">
                    {plainProseFromMathlive(
                      sanitizeMathliveForKatex(content.text),
                    )}
                  </div>
                ) : mixedPartsFromMathlive(content.text) !== null ? (
                  <MixedMathliveMessage
                    lines={mixedPartsFromMathlive(content.text) ?? []}
                  />
                ) : (
                  // Composer-typed messages: render through the same
                  // markdown+LaTeX pipeline as assistant bubbles (Mike, 14
                  // Sep) so Formula-mode input typesets instead of showing
                  // raw source. plainText / recommendation seeds keep their
                  // plain-div escape hatches above.
                  <CommonMarkdownLatexComponent
                    text={sanitizeMathliveForKatex(content.text)}
                  />
                )}
              </>
            )}

            {/* Images */}
            <div className="display-user-answer">
              {!!content.imageUrls?.length && (
                <div className="flex flex-wrap gap-4">
                  {content.imageUrls.map((imageUrl, i) => {
                    return (
                      <Fragment key={`${i}-${id}-${imageUrl}`}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          key={`${i}-${id}-${imageUrl}`}
                          src={imageUrl}
                          alt={`Uploaded content_img`}
                          className="my-2 w-full h-full max-h-96 max-w-64 rounded-lg object-cover object-center cursor-pointer"
                          onClick={() =>
                            content.imageUrls &&
                            handleImageClick &&
                            handleImageClick(content.imageUrls, i)
                          }
                        />
                      </Fragment>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Mobile message teal-like pointer (points UP) */}
          <div className="sm:hidden absolute top-[-6px] right-0 w-0 h-0 border-lime-200 border-b-0 border-l-0 border-r-[10px] border-t-[10px] border-t-transparent"></div>
          {/* Desktop message teal-like pointer (points RIGHT) */}
          <div className="hidden sm:block absolute top-0 right-[-5px] w-0 h-0 border-lime-200 border-l-[10px] border-b-[10px] border-r-0 border-t-0 border-b-transparent"></div>
        </div>
      </div>

      {/* Avatar */}
      {user ? (
        <>
          {/* User Avatar Img */}
          {user.photoURL ? (
            <div className="relative rounded-full min-h-8 min-w-8 overflow-hidden">
              <Image
                src={user.photoURL}
                fill
                className="object-cover"
                alt="user-avatar"
              />
            </div>
          ) : (
            <>
              {/* Generate Avatar From Initials */}
              {!!nameForAvatarInitials ? (
                <AvatarWithName
                  className="bg-v8-blue/15 rounded-full h-8 w-8 min-h-8 min-w-8"
                  textClassName="text-xs text-v8-blue"
                  name={nameForAvatarInitials}
                />
              ) : (
                <>
                  {/* Default Avatar */}
                  <UserIcon className="p-1 bg-v8-blue/15 rounded-full h-8 w-8 min-h-8 min-w-8 fill-v8-blue" />
                </>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {/* Default Avatar for Non-Auth Users */}
          <UserIcon className="p-1 bg-v8-blue/15 rounded-full h-8 w-8 min-h-8 min-w-8 fill-v8-blue" />
        </>
      )}
    </div>
  )
}
